// src/components/common/modal/DialogBase.tsx

import React, { useRef, useState, useCallback, useEffect, useId } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  IconButton,
  styled,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { colors } from '../../../theme/theme';

interface DialogBaseProps {
  open: boolean;
  onClose: () => void;
  title: string;
  headerIcon?: React.ReactNode;
  headerColor?: 'primary' | 'secondary' | 'error';
  children: React.ReactNode;
  footer?: React.ReactNode;
  maxWidth?: 'xs' | 'sm' | 'md';
  fullWidth?: boolean;
  // When true, the dialog fills the entire viewport (no margin, no
  // rounded corners, no drop shadow). Drag is auto-disabled since
  // there's nowhere to drag a viewport-filling dialog to, and the
  // close button moves from edge-overlap to an inset position so it
  // remains fully tappable. Defaults to false; existing callers see
  // no behaviour change.
  fullScreen?: boolean;
  draggable?: boolean;
  showCloseButton?: boolean;
  // Per-instance style override for the inner MuiDialogContent-root.
  // Defaults to undefined, which keeps the standard padding
  // ('5px 24px 20px 24px'). Pass `{ padding: 0 }` for edge-to-edge
  // body content (used by ConvertQuoteDialog's prospect path to let
  // alternating-row backgrounds extend to the dialog edges).
  contentSx?: SxProps<Theme>;
}

const DialogHeader = styled(Box)(({ theme }) => ({
  color: theme.palette.primary.contrastText,
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  padding: theme.spacing(1.25),
  borderTopLeftRadius: '4px',
  borderTopRightRadius: '4px',
  minWidth: 0,
  userSelect: 'none',
  flexShrink: 0,
}));

const DialogHeaderIcon = styled(Box)(({ theme }) => ({
  width: '3rem',
  height: '3rem',
  opacity: 0.9,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginRight: theme.spacing(1),
  flexShrink: 0,
  '& svg': {
    fontSize: '2.5rem',
  },
}));

const DialogTitleText = styled(Typography)(() => ({
  fontSize: '2rem',
  fontWeight: 600,
  color: 'inherit',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  minWidth: 0,
}));

const DialogFooter = styled(Box)(({ theme }) => ({
  flexShrink: 0,
  position: 'relative',
  padding: `${theme.spacing(2)}`,
  borderTop: `3px dashed ${theme.palette.dividerContrast}`,
  display: 'flex',
  justifyContent: 'space-around',
  alignItems: 'center',
  backgroundColor: theme.palette.grey[0],
  borderBottomLeftRadius: '.5rem',
  borderBottomRightRadius: '.5rem',
  [theme.breakpoints.down('sm')]: {
    justifyContent: 'center',
    gap: theme.spacing(2),
  },
}));

const DialogBase: React.FC<DialogBaseProps> = ({
  open,
  onClose,
  title,
  headerIcon,
  headerColor = 'primary',
  children,
  footer,
  maxWidth = 'xs',
  fullWidth = true,
  fullScreen = false,
  draggable = false,
  showCloseButton = true,
  contentSx,
}) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));
  // DialogTitleText is a plain styled(Typography) with no heading role and
  // no link to the Dialog's own accessible name - unlike MUI's own
  // DialogTitle (used by dialogs that don't go through DialogBase), this
  // left every DialogBase-based dialog's title unannounced as a heading
  // to assistive tech, and the Dialog itself with no accessible name at
  // all. titleId wires both: a real heading role on the title, and
  // aria-labelledby on the Dialog root pointing at it.
  const titleId = useId();
  // Drag is meaningless when the dialog fills the viewport; explicitly
  // veto it so the header doesn't render a grab cursor.
  const canDrag = draggable && isDesktop && !fullScreen;

  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStart = useRef({ x: 0, y: 0 });

  useEffect(() => {
    if (open) setPosition({ x: 0, y: 0 });
  }, [open]);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (!canDrag) return;
      setIsDragging(true);
      dragStart.current = {
        x: e.clientX - position.x,
        y: e.clientY - position.y,
      };
    },
    [canDrag, position]
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging) return;
      setPosition({
        x: e.clientX - dragStart.current.x,
        y: e.clientY - dragStart.current.y,
      });
    },
    [isDragging]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      fullScreen={fullScreen}
      aria-labelledby={titleId}
      sx={{
        '& .MuiDialog-paper': {
          padding: 0,
          // No rounded corners or drop shadow when the dialog fills the
          // viewport - there's no edge to round.
          borderRadius: fullScreen ? 0 : 1,
          boxShadow: fullScreen ? 'none' : theme.shadows[8],
          overflow: showCloseButton ? 'visible' : 'hidden',
          ...(canDrag && {
            transform: `translate(${position.x}px, ${position.y}px)`,
            transition: isDragging ? 'none' : 'transform 0.15s ease-out',
          }),
        },
        '& .MuiBackdrop-root': {
          backgroundColor: 'rgba(0, 0, 0, 0.85)',
        },
      }}
    >
      <DialogHeader
        onMouseDown={handleMouseDown}
        sx={{
          backgroundColor: theme.palette[headerColor].main,
          ...(canDrag && {
            cursor: isDragging ? 'grabbing' : 'grab',
          }),
        }}
      >
        {headerIcon && <DialogHeaderIcon>{headerIcon}</DialogHeaderIcon>}
        <DialogTitleText id={titleId} role="heading" aria-level={2}>
          {title}
        </DialogTitleText>
      </DialogHeader>

      {showCloseButton && (
        <IconButton
          onClick={onClose}
          onMouseDown={(e) => e.stopPropagation()}
          size="small"
          sx={{
            position: 'absolute',
            // When the dialog fills the viewport the standard edge-overlap
            // (-4, -4) puts the button half off-screen. Inset it so it
            // stays fully tappable on touch devices.
            top: fullScreen ? 8 : -4,
            right: fullScreen ? 8 : -4,
            zIndex: 1,
            width: '2.7rem',
            height: '2.7rem',
            backgroundColor: theme.palette[headerColor].main,
            border: `3px solid ${theme.palette[headerColor].light}40`,
            boxShadow: theme.shadows[6],
            color: theme.palette.grey[0],
            '&:hover': {
              backgroundColor: theme.palette[headerColor].dark,
              boxShadow: colors.shadows.powerInsetBold,
              border: `3px solid ${theme.palette[headerColor].light}`,
            },
            '& svg': { fontSize: '2rem' },
          }}
        >
          <CloseIcon />
        </IconButton>
      )}

      <DialogContent
        sx={{
          // Default padding kept for all existing callers; contentSx
          // wins for callers that opt to override.
          padding: '5px 24px 20px 24px',
          ...contentSx,
        }}
      >
        {children}
      </DialogContent>

      {footer && <DialogFooter>{footer}</DialogFooter>}
    </Dialog>
  );
};

export default DialogBase;
